import { useState } from "react";
import { DEFAULT_FILTERS, getSelectedIds } from "../utils";

export const usePOFilter = (venues, roleRates) => {
  const [filters, setFilters] = useState(DEFAULT_FILTERS.FILTERS);
  const [openDropdowns, setOpenDropdowns] = useState(DEFAULT_FILTERS.DROPDOWNS);

  // Selected IDs
  const selectedVenuesIds = getSelectedIds("venueFilter", filters);
  const selectedRoleRateIds = getSelectedIds("roleFilter", filters);
  const selectedSubvenuesIds = getSelectedIds("subvenueFilter", filters);

  // Check if any filter is selected
  const isAnyFilterSelected = Object.values(filters).some(
    filter => filter.selectedItems.length
  );

  // Toggle dropdown
  const toggleDropdown = name => {
    if (openDropdowns.main === true && name === "main") {
      setOpenDropdowns(DEFAULT_FILTERS.DROPDOWNS);
    } else {
      setOpenDropdowns({ ...openDropdowns, [name]: !openDropdowns[name] });
    }
  };

  // Handle search within filter
  const handleSearch = (value, filterName) => {
    setFilters({
      ...filters,
      [filterName]: {
        ...filters[filterName],
        searchTerm: value
      }
    });
  };

  // Helper function to check if an item is already selected
  const isItemSelected = (itemValue, filterName) => {
    return getSelectedIds(filterName, filters).includes(itemValue);
  };

  // Helper function to add an item to a filter's selected items
  const addItemToFilter = (filterName, item) => {
    return {
      ...filters,
      [filterName]: {
        ...filters[filterName],
        selectedItems: [...filters[filterName].selectedItems, item]
      }
    };
  };

  // Helper function to remove an item from a filter's selected items
  const removeItemFromFilter = (filterName, itemValue) => {
    return {
      ...filters,
      [filterName]: {
        ...filters[filterName],
        selectedItems: filters[filterName].selectedItems.filter(
          item => item.value !== itemValue
        )
      }
    };
  };

  // Function to handle venue deselection and clean up related subvenues
  const handleVenueDeselection = venueId => {
    let updatedFilters = removeItemFromFilter("venueFilter", venueId);

    // Find subvenues belonging to this venue
    const subvenuesToRemove =
      venues
        ?.find(venue => venue.id === venueId)
        ?.subvenues?.map(subvenue => subvenue.id) || [];

    // Remove any subvenues belonging to the deselected venue
    if (subvenuesToRemove.length > 0) {
      updatedFilters = {
        ...updatedFilters,
        subvenueFilter: {
          ...updatedFilters.subvenueFilter,
          selectedItems: updatedFilters.subvenueFilter.selectedItems.filter(
            item => !subvenuesToRemove.includes(item.value)
          )
        }
      };
    }

    return updatedFilters;
  };

  // Function to handle venue selection and validate subvenues
  const handleVenueSelection = venueItem => {
    let updatedFilters = addItemToFilter("venueFilter", venueItem);

    // Get all valid subvenue IDs from all selected venues
    const allValidSubvenueIds = venues
      ?.filter(venue =>
        updatedFilters.venueFilter.selectedItems.some(
          item => item.value === venue.id
        )
      )
      .flatMap(venue => venue.subvenues?.map(subvenue => subvenue.id) || []);

    // Keep only valid subvenues
    updatedFilters = {
      ...updatedFilters,
      subvenueFilter: {
        ...updatedFilters.subvenueFilter,
        selectedItems: updatedFilters.subvenueFilter.selectedItems.filter(
          item => allValidSubvenueIds.includes(item.value)
        )
      }
    };

    return updatedFilters;
  };

  // Main selection handler function
  const handleSelect = (selectedItem, filterName) => {
    // Special handling for venue filter
    if (filterName === "venueFilter") {
      const isAlreadySelected = isItemSelected(selectedItem.value, filterName);
      const updatedFilters = isAlreadySelected
        ? handleVenueDeselection(selectedItem.value)
        : handleVenueSelection(selectedItem);

      setFilters(updatedFilters);
    }
    // Standard handling for other filter types
    else {
      const isAlreadySelected = isItemSelected(selectedItem.value, filterName);
      const updatedFilters = isAlreadySelected
        ? removeItemFromFilter(filterName, selectedItem.value)
        : addItemToFilter(filterName, selectedItem);

      setFilters(updatedFilters);
    }
  };

  // Clear filter
  const handleClear = filterName => {
    setFilters({
      ...filters,
      [filterName]: {
        ...filters[filterName],
        selectedItems: []
      }
    });
  };

  // Generate dropdown options based on current selections
  const dropdownOptions = {
    venueFilter: venues
      ?.map(venue => ({
        value: venue.id,
        label: venue.name,
        selected: selectedVenuesIds.includes(venue.id)
      }))
      .sort((a, b) =>
        a.label.toLowerCase().localeCompare(b.label.toLowerCase())
      ),
    subvenueFilter: (() => {
      // Get all subvenues if no venues selected
      if (!selectedVenuesIds.length) {
        return venues
          ?.flatMap(
            venue =>
              venue.subvenues?.map(subvenue => ({
                value: subvenue.id,
                label: subvenue.name,
                parent: venue.name,
                parentId: venue.id,
                selected: selectedSubvenuesIds.includes(subvenue.id)
              })) || []
          )
          .sort((a, b) =>
            a.label.toLowerCase().localeCompare(b.label.toLowerCase())
          );
      }
      // Otherwise only show subvenues from selected venues
      else {
        return venues
          ?.filter(venue => selectedVenuesIds.includes(venue.id))
          .flatMap(
            venue =>
              venue.subvenues?.map(subvenue => ({
                value: subvenue.id,
                label: subvenue.name,
                parent: venue.name,
                parentId: venue.id,
                selected: selectedSubvenuesIds.includes(subvenue.id)
              })) || []
          )
          .sort((a, b) =>
            a.label.toLowerCase().localeCompare(b.label.toLowerCase())
          );
      }
    })(),
    roleFilter: roleRates
      ?.map(roleRate => ({
        value: roleRate.id,
        label: roleRate.role.name,
        selected: selectedRoleRateIds.includes(roleRate.id)
      }))
      .sort((a, b) =>
        a.label.toLowerCase().localeCompare(b.label.toLowerCase())
      )
  };

  return {
    filters,
    openDropdowns,
    isAnyFilterSelected,
    dropdownOptions,
    selectedVenuesIds,
    selectedRoleRateIds,
    selectedSubvenuesIds,
    toggleDropdown,
    handleSearch,
    handleSelect,
    handleClear
  };
};
